import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  assistedOtherPhrase,
  assistedVisibleWords,
  assistedWordChunks,
  correctionChangedWords,
  type CorrectionReviewMatch,
} from '@orborix/shared-types';
import type { ConversationPrefs } from '@orborix/shared-types';
import {
  getConversationPrefs,
  saveConversationPrefs,
  speakTextWithAlignment,
  type SpokenAudio,
} from '../../../lib/englishApi.js';
import { useAudioBlob } from '../../../lib/audioBlobPlayer.js';
import { charIndexAt, replayAlignment } from '../replayKaraoke.js';
import { SpokenBody } from '../messages/SpokenBody.js';
import { IcRadar } from '../../progress/radarIcons.js';
import { useMicWavCapture } from '../../../lib/micWavCapture.js';
import { TAP_SILENCE_MS } from '../../../lib/silenceEnder.js';
import { POST_ROLL_MS } from '@orborix/voice-composer';
import { play as playCelebration } from '../../../lib/celebrations/index.js';
import { sfx } from '../../../lib/sfx/index.js';
import { SFX_SPECS } from '../../../lib/sfx/sounds.js';
import { Waveform } from '../../../components/doodles/Doodles.js';
import { IconBookmarkSmall, IconMicSolid, IconX } from '../icons.js';
import { ASSISTED_COPY, reviewCopyFor, type AssistedCopy } from './assistedCopy.js';
import { fetchReviewCues } from '../../../lib/correctionReviewApi.js';
import {
  SOLO_TRIES,
  useCoachReview,
  type CoachReviewItem,
  type ReviewGuideCopy,
  type ReviewResult,
} from './useCoachReview.js';
import { soloNote } from './reviewSoloNote.js';
import { mergeSamePhrase, spreadResults } from './reviewSamePhrase.js';
import { DONE_LINE, linesFor, openLine } from './reviewCoachLines.js';
import { loadReviewAhead, type ReviewAhead } from './reviewAhead.js';
import { notifyProgressChanged } from '../../../lib/progressRefresh.js';
import { returnsDay } from '../../../lib/reviewReturns.js';
import { karaokeMarksUpTo, type KaraokeMark } from './assistedModel.js';
import { useReviewLive, type ReviewLiveState } from './useReviewLive.js';
import { takeLimitMs } from './reviewTake.js';
import { takeWarmReviewVoice, warmReviewVoice } from './reviewVoice.js';
import './assisted.css';
import './coachReview.css';

/** Los textos del repaso de esta apertura (las palabras guardadas de la nave tienen los suyos). */
const CopyContext = createContext<AssistedCopy>(ASSISTED_COPY.es);
const useCopy = (): AssistedCopy => useContext(CopyContext);
/**
 * Soltar antes de esto = un toque: la toma queda abierta hasta el próximo
 * toque, como el micrófono de la charla (`useHoldGesture`). Antes el toque la
 * cortaba en el acto y el repaso decía «No te escuché» mientras el alumno
 * hablaba (Emmanuel, 14/10).
 */
const TAP_LOCK_MS = 450;
/**
 * Cuánto dura la celebración de un acierto sin texto para leer antes de seguir
 * sola (Emmanuel, 15/10: con los 1,5 s de la maqueta «pasa muy rápido»). Con
 * texto, no sigue sola: espera «Siguiente».
 */
const WIN_MS = 2300;
/** Los efectos van encima del repaso (su fondo está en z 80). */
const FX_Z = 95;
/**
 * La voz de una línea nueva se genera la primera vez (2-4 s): pasado esto sin
 * sonar, la línea va entera y el karaoke sólo resalta (encargo 113: aparecía
 * recién después de que el alumno ya la había dicho). Una línea cuya voz todavía
 * no llegó al pedirla va entera desde el principio (card #595); esto queda para
 * la que ya había llegado y tarda en arrancar.
 */
const VOICE_WAIT_MS = 1000;
/** Texto entero de entrada: el karaoke sólo mueve el resaltado. */
const FULL_REVEAL = (): number => Infinity;
/**
 * Las voces de antemano esperan a la que está por sonar (card #595: pedidas las
 * diez juntas, la primera línea tardó 6,8 s; sola, ~3 s), pero no más que esto.
 */
const PREFETCH_AFTER_MAX_MS = 4000;
/** El sonido del festejo sin mirar sale a los 350 ms (cuando el server ya dijo si quedó aprendida). */
const CELEBRATION_SOUND_AT_MS = 350;
/**
 * La voz del coach después del festejo sin mirar, cuando el sonido ya se oyó
 * (card #595): sonaban juntos y la voz lo tapaba entero, y el acierto con la
 * frase a la vista (sin voz encima) parecía más fuerte. Medido el 21/10: misma
 * ganancia y pico en todos; el de sin mirar, incluso 1,7 LU más fuerte.
 */
const VOICE_AFTER_CELEBRATION_MS =
  CELEBRATION_SOUND_AT_MS + SFX_SPECS['celebrate-small'].len * 1000;

/** Una corrección para repasar; la pista en español la trae el repaso al abrir. */
export type CoachReviewInput = Omit<CoachReviewItem, 'cue' | 'hint'>;

type ReviewSource = 'close' | 'day' | 'game' | 'radar' | 'words';

/**
 * Pide de antemano la voz de la primera línea del coach (card #595): la usa el
 * Inicio al dibujar el Repaso del día y el repaso al abrir, antes de las pistas.
 * Si la primera tarjeta cambia con las pistas (salteada o juntada), se pidió una
 * línea que no suena: queda guardada en el server, no se cobra otra vez.
 */
export function warmReviewOpening(
  items: ReadonlyArray<CoachReviewInput>,
  source: ReviewSource,
): void {
  const first = items[0];
  if (!first) return;
  warmReviewVoice(
    openLine(0, first.returning === true, source === 'words' ? 'words' : 'talks', first.kind === 'word'),
  );
}

interface CoachReviewProps {
  items: ReadonlyArray<CoachReviewInput>;
  /** Del cierre, del Repaso del día, del final de una partida o de un foco del Radar (el nombre del diálogo). */
  source: ReviewSource;
  finishLabel: string;
  /** Al cerrar (terminado o no): lo que se decidió de cada corrección. */
  onExit: (results: ReadonlyArray<ReviewResult>) => void;
}

/**
 * El repaso de las correcciones CON EL COACH (charla asistida, forma 2 de
 * #468), con la forma de la maqueta A · Escenario que eligió Emmanuel (15/10):
 * arriba sólo lo último que dice el coach, grande, con las ondas y «COACH» de
 * su burbuja en la charla; abajo la frase en una tarjeta y el micrófono
 * gigante; aciertos con sello, confeti y sonidos; y al final el resumen del
 * repaso (sin puntos ni racha de aciertos desde la card #604: el 🔥 es el de los
 * días seguidos del perfil). Va en un
 * portal a `body` (el cierre vive dentro de `.db-reveal`, cuyo transform sería
 * el containing block de un `fixed`).
 *
 * Por corrección, dos vueltas (`useCoachReview`): con la frase a la vista y
 * SIN MIRAR, con la pista en español y casilleros que se dan vuelta al
 * acertar. Lo dicho sin mirar decide cuándo vuelve (`PATCH /corrections/state`).
 *
 * La voz del coach (pedido de Emmanuel, 14/10): mientras se genera, tres
 * puntitos; cuando suena, la línea se revela tipo karaoke, como en la charla
 * (tiempos por carácter de `/speak`; sin ellos, proporcional a la duración).
 * Suena una sola cosa a la vez (`useAudioBlob`), se calla al apretar el
 * micrófono y no sigue de una corrección a la otra.
 */
export function CoachReview({ items, source, finishLabel, onExit }: CoachReviewProps) {
  const copy = useMemo(() => reviewCopyFor(source), [source]);
  // La pista en español de cada corrección (la del «sin mirar») y si ya vio la
  // explicación del juego. Si la pista no llega, el repaso sigue sin ella.
  const [ready, setReady] = useState<{
    items: ReadonlyArray<CoachReviewItem>;
    guide: ReviewGuideCopy | null;
    /** Las tarjetas que cada una absorbió por terminar en la misma frase (`mergeSamePhrase`). */
    absorbed: ReadonlyMap<string, ReadonlyArray<CoachReviewItem>>;
  } | null>(null);
  const [howTo, setHowTo] = useState(false);
  // Las correcciones son las de cuando se abrió el repaso.
  const itemsRef = useRef(items);
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  useEffect(() => {
    let alive = true;
    const items = itemsRef.current;
    // La voz de la primera línea, ya: no espera a las pistas (card #595).
    warmReviewOpening(items, source);
    // Con la pista llega lo que se repasa: la corrección con su oración entera
    // (Emmanuel, 20/10; también las de varias palabras, card #571), en los cuatro repasos.
    const withCues = fetchReviewCues(items.map((i) => i.id))
      .then((q) =>
        // Sin ningún pedazo que se diga solo (card #580): la tarjeta no va.
        items
          .filter((i) => !q.skip.includes(i.id))
          .map((i) => ({
            ...i,
            ...q.forms[i.id],
            cue: q.cues[i.id] ?? null,
            hint: q.hints[i.id] ?? null,
          })),
      )
      .catch((err: unknown) => {
        console.warn('[review] cues failed — the review goes without them', err);
        return items.map((i) => ({ ...i, cue: null, hint: null }));
      });
    // La explicación sale UNA vez por cuenta (Emmanuel, 16/10). Sin poder leer
    // la preferencia, sale igual (no molesta) y no se marca.
    const prefs = getConversationPrefs().catch((err: unknown) => {
      console.warn('[review] prefs failed — the guide shows, unsaved', err);
      return null;
    });
    void Promise.all([withCues, prefs]).then(([withCues, prefs]) => {
      if (!alive) return;
      // Una vez por cuenta, también con las palabras guardadas (la línea del coach ya dice qué son; y
      // en un celular chico la explicación empujaba el micrófono fuera de la pantalla).
      const guide = prefs?.reviewGuideSeen === true ? null : copy.review;
      if (prefs && guide) markGuideSeen(prefs);
      // Con la frase entera, dos que terminan en la misma frase son una tarjeta (card #571).
      const merged = mergeSamePhrase(withCues);
      // Todas salteadas: no hay repaso.
      if (merged.items.length === 0) {
        onExitRef.current([]);
        return;
      }
      setReady({ items: merged.items, guide, absorbed: merged.absorbed });
    });
    return () => {
      alive = false;
    };
  }, []);

  if (ready) {
    return (
      <CopyContext.Provider value={copy}>
        <CoachReviewSession
          items={ready.items}
          guide={ready.guide}
          source={source}
          finishLabel={finishLabel}
          onExit={(results) => onExit(spreadResults(results, ready.absorbed))}
        />
      </CopyContext.Provider>
    );
  }
  return createPortal(
    <CopyContext.Provider value={copy}>
    <Shell source={source}>
      <Top
        items={items}
        index={0}
        results={[]}
        onClose={() => onExit([])}
        onHowTo={() => setHowTo(true)}
      />
      <div className="rv-coach-space">
        <Typing />
      </div>
      {howTo && <HowToSheet onClose={() => setHowTo(false)} />}
    </Shell>
    </CopyContext.Provider>,
    document.body,
  );
}

/** La explicación ya salió: no vuelve en el próximo repaso (ni en otro dispositivo). */
function markGuideSeen(prefs: ConversationPrefs): void {
  saveConversationPrefs({ ...prefs, reviewGuideSeen: true }).catch((err: unknown) =>
    console.warn('[review] could not save that the guide was seen', err),
  );
}

function CoachReviewSession({
  items,
  guide,
  source,
  finishLabel,
  onExit,
}: Omit<CoachReviewProps, 'items'> & {
  items: ReadonlyArray<CoachReviewItem>;
  guide: ReviewGuideCopy | null;
}) {
  const copy = useCopy();
  // La voz del coach (sus indicaciones) y «Escuchar»: `play` corta lo que sonaba
  // o cargaba (sólo suena la ÚLTIMA pedida) y el hook la apaga al desmontar. Se
  // lee por ref: `speak`/`hush` estables.
  const player = useAudioBlob();
  const playerRef = useRef(player);
  playerRef.current = player;
  const alignRef = useRef<ReadonlyArray<number> | null>(null);
  const [voicing, setVoicing] = useState<string | null>(null);
  // La línea cuya voz tardó más de VOICE_WAIT_MS (se muestra entera).
  const [slowFor, setSlowFor] = useState<string | null>(null);
  const voicingRef = useRef<string | null>(null);
  voicingRef.current = voicing;
  // Las voces quedan guardadas mientras dura el repaso, y lo que viene se pide de
  // antemano (`prefetch`): las líneas del coach son fijas, así que el server las
  // tiene guardadas para siempre después de la primera vez. Un pedido que falla
  // no queda guardado.
  const voicesRef = useRef(new Map<string, Promise<SpokenAudio>>());
  /** Las voces que ya llegaron (las demás: la línea se lee entera mientras llega). */
  const readyRef = useRef(new Set<string>());
  const voiceOf = useCallback((text: string): Promise<SpokenAudio> => {
    const known = voicesRef.current.get(text);
    if (known) return known;
    // La primera línea ya se pidió al abrir (o desde el Inicio); si no, ahora.
    // Voz del repaso: el server la guarda para siempre (se genera y se cobra una vez).
    const req = takeWarmReviewVoice(text) ?? speakTextWithAlignment(text, { use: 'review' });
    voicesRef.current.set(text, req);
    req.then(
      () => readyRef.current.add(text),
      () => voicesRef.current.delete(text),
    );
    return req;
  }, []);
  // La voz de lo que está por sonar: lo de antemano se pide después (o pasado
  // PREFETCH_AFTER_MAX_MS), así no compite con ella.
  const sayingRef = useRef<Promise<unknown>>(Promise.resolve());
  const prefetch = useCallback(
    (texts: readonly string[]): void => {
      const cap = new Promise<void>((resolve) => setTimeout(resolve, PREFETCH_AFTER_MAX_MS));
      void Promise.race([sayingRef.current, cap]).then(() => {
        for (const t of texts) voiceOf(t).catch(() => {});
      });
    },
    [voiceOf],
  );
  // La voz que espera al festejo (`afterCelebration`): la corta otra línea, callarlo o cerrar.
  const heldRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const release = (): void => {
    if (heldRef.current) clearTimeout(heldRef.current);
    heldRef.current = null;
  };
  useEffect(() => release, []);
  const speak = useCallback(
    (text: string, opts?: { afterCelebration?: boolean }): void => {
      release();
      alignRef.current = null;
      setVoicing(text);
      const voice = voiceOf(text);
      // Sin la voz todavía (la primera vez de esa línea con esa voz y ese tono: ~3 s), la línea
      // se lee ya, entera, y la voz entra cuando llega; el micrófono no la espera (card #595).
      setSlowFor(readyRef.current.has(text) ? null : text);
      sayingRef.current = voice.catch(() => {});
      const start = (): void => {
        heldRef.current = null;
        void playerRef.current.play(async () => {
          const spoken = await voice;
          alignRef.current = replayAlignment(spoken.startsMs);
          return spoken.blob;
        });
      };
      if (!opts?.afterCelebration) {
        start();
        return;
      }
      // Mientras, la línea se lee entera (sin los puntitos de «cargando»).
      playerRef.current.stop();
      setSlowFor(text);
      heldRef.current = setTimeout(start, VOICE_AFTER_CELEBRATION_MS);
    },
    [voiceOf],
  );
  const hush = useCallback((): void => {
    release();
    playerRef.current.stop();
    setVoicing(null);
  }, []);
  useEffect(() => {
    if (player.state === 'error') console.warn('[review] coach voice failed', player.error);
  }, [player.state, player.error]);
  useEffect(() => {
    if (voicing === null || player.state !== 'loading') return;
    const t = window.setTimeout(() => setSlowFor(voicing), VOICE_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [voicing, player.state]);
  // Karaoke: el carácter que suena ahora (los tiempos de `/speak`; sin ellos,
  // proporcional a la duración) y hasta dónde se revela (el fin de esa palabra).
  const getCharIndex = useCallback((): number => {
    const p = playerRef.current;
    const pos = p.getPositionMs();
    if (pos < 0) return -1;
    const starts = alignRef.current;
    if (starts) return charIndexAt(starts, pos);
    const len = voicingRef.current?.length ?? 0;
    const dur = p.getDurationMs();
    return dur > 0 && len > 0 ? Math.min(len - 1, Math.floor((pos / dur) * len)) : -1;
  }, []);
  const getRenderEndIdx = useCallback((): number => {
    const ci = getCharIndex();
    if (ci < 0) return 0;
    const text = voicingRef.current ?? '';
    const space = text.indexOf(' ', ci);
    return space < 0 ? text.length : space;
  }, [getCharIndex]);

  const review = useCoachReview(items, {
    speak,
    hush,
    guide,
    ...(source === 'radar' ? { via: 'radar' as const } : {}),
    ...(source === 'words' ? { words: true } : {}),
    ...(source === 'day' || source === 'words' ? { timed: source } : {}),
  });
  const lineKind = source === 'words' ? ('words' as const) : ('talks' as const);
  const [howTo, setHowTo] = useState(false);
  // De antemano: lo que puede decir el coach en esta corrección y al empezar la
  // siguiente (o al terminar).
  useEffect(() => {
    const it = items[review.index];
    if (!it) return;
    const nextItem = items[review.index + 1];
    prefetch([
      ...linesFor(review.index, it.returning === true, lineKind, it.kind === 'word'),
      nextItem
        ? openLine(review.index + 1, nextItem.returning === true, lineKind, nextItem.kind === 'word')
        : DONE_LINE,
    ]);
  }, [review.index, items, prefetch, lineKind]);
  const capture = useMicWavCapture(takeLimitMs(items[review.index]?.corrected ?? ''));
  // Las palabras en vivo mientras habla (Emmanuel, 15/10): sólo muestran.
  const live = useReviewLive();
  const [micError, setMicError] = useState(false);
  const [closing, setClosing] = useState(false);
  const [ahead, setAhead] = useState<ReviewAhead | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const { item, phase, round, match, index } = review;
  const current = item ? review.results.find((r) => r.item.id === item.id) : undefined;

  // Sonidos y animaciones de la tarjeta en cada cambio de fase. Sin racha de
  // aciertos ni puntos (card #604, opción 3 de los fueguitos: el 🔥 es el de los días seguidos).
  const retry = round === 'solo' && review.triesLeft < SOLO_TRIES;
  /** Quedó alguna sin descartar (si no, no hubo repaso). */
  const anyLeft = review.total > 0;
  // Al final, con lo de hoy guardado: los días seguidos y cuántas mañana (card #604).
  const settled = review.settled;
  useEffect(() => {
    if (phase !== 'done' || !anyLeft) return;
    let alive = true;
    void settled()
      .then(loadReviewAhead)
      .then((a) => {
        if (alive) setAhead(a);
      });
    return () => {
      alive = false;
    };
  }, [phase, anyLeft, settled]);
  const prevPhaseRef = useRef(phase);
  useEffect(() => {
    const was = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (phase === was) return;
    if (phase === 'accepted') {
      // UN sonido por acierto (Emmanuel, 15/10: sonaban tres seguidos). Sin mirar
      // al primer intento lo pone la celebración (festejo, o el grande si quedó
      // aprendida); si no, éste.
      if (!(round === 'solo' && !retry)) sfx.play('game-correct');
    } else if (phase === 'failed') {
      sfx.play('game-miss');
      if (match?.state !== 'silence') animate(cardRef.current, SHAKE, 450);
    } else if (phase === 'seen') {
      sfx.play('card-flip');
    } else if (phase === 'done' && anyLeft) {
      // Todas descartadas: no hubo repaso, no hay festejo (el repaso se cierra solo).
      sfx.play('level-up');
      playCelebration('confetti-rain', { z: FX_Z });
    }
  }, [phase, round, retry, match, anyLeft]);
  // A la vuelta sin mirar, la tarjeta se da vuelta. Sin sonido: llega justo
  // después de un acierto, que ya sonó (lo mismo al pasar a la siguiente).
  const prevStepRef = useRef({ index, round });
  useEffect(() => {
    const was = prevStepRef.current;
    prevStepRef.current = { index, round };
    if (index === was.index && round === 'solo' && was.round === 'ayuda') {
      animate(cardRef.current, FLIP, 550);
    }
  }, [index, round]);

  // La celebración de un acierto: confeti de cañones sin mirar al primer
  // intento; brillitos en la tarjeta si no. Después sigue sola.
  const doneRef = useRef(review.celebrationDone);
  doneRef.current = review.celebrationDone;
  // Se fija al aceptar: no cambia mientras celebra.
  const retryRef = useRef(retry);
  retryRef.current = retry;
  const learnedNow = phase === 'accepted' && current?.learned === true;
  const learnedRef = useRef(learnedNow);
  learnedRef.current = learnedNow;
  // Con texto para leer no sigue sola, espera «Siguiente» (Emmanuel, 15/10: «es
  // imposible leerlo»): sin mirar siempre dice cuándo vuelve, y bien de otra
  // forma dice qué valió y qué más se podía decir.
  const waitsForNext = phase === 'accepted' && (round === 'solo' || match?.state === 'ok-judged');
  const waitsRef = useRef(waitsForNext);
  waitsRef.current = waitsForNext;
  useEffect(() => {
    if (phase !== 'accepted') return;
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    if (round === 'solo' && !retryRef.current) {
      playCelebration('confetti-cannons', { z: FX_Z });
      // Para entonces el server ya dijo si quedó aprendida (el PATCH tarda menos).
      timers.push(
        setTimeout(
          () => sfx.play(learnedRef.current ? 'celebrate-big' : 'celebrate-small'),
          CELEBRATION_SOUND_AT_MS,
        ),
      );
    } else {
      const target = cardRef.current ?? undefined;
      playCelebration('sparkles', { ...(target ? { target } : {}), count: 18, z: 5 });
    }
    if (!waitsRef.current) timers.push(setTimeout(() => doneRef.current(), WIN_MS));
    return () => timers.forEach(clearTimeout);
  }, [phase, round]);
  // Aprendida (3 vueltas de memoria; lo dice el server al guardar): fuegos
  // artificiales (el sonido es el de la celebración, no otro más).
  useEffect(() => {
    if (!learnedNow) return;
    playCelebration('fireworks', { count: 5, z: FX_Z });
  }, [learnedNow]);

  // La toma en curso (por ref: el soltar de un toque rápido puede llegar antes
  // del re-render) y el post-roll de la que se está cerrando.
  const takeRef = useRef<{ downAt: number; locked: boolean } | null>(null);
  const postRollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [locked, setLocked] = useState(false);
  useEffect(
    () => () => {
      if (postRollRef.current) clearTimeout(postRollRef.current);
    },
    [],
  );

  /**
   * Cierra la toma. Sigue grabando `POST_ROLL_MS` (el de la charla): con
   * auriculares inalámbricos lo último que se dijo llega tarde al navegador.
   */
  const endTake = (): void => {
    takeRef.current = null;
    setLocked(false);
    if (postRollRef.current) return;
    postRollRef.current = setTimeout(() => {
      postRollRef.current = null;
      capture.stop();
    }, POST_ROLL_MS);
  };

  const canTalk = phase === 'ask' || phase === 'failed' || phase === 'seen' || phase === 'error';
  const holdStart = (): void => {
    if (takeRef.current?.locked) {
      endTake(); // el segundo toque manda
      return;
    }
    if (postRollRef.current) return; // la toma anterior todavía se está cerrando
    if (!canTalk || !item) return;
    setMicError(false);
    // Sin sonido al abrir el micrófono (la maqueta hacía «tic»), por las dudas:
    // el 15/10 seis tomas de Emmanuel llegaron mudas al server y el tic era lo
    // único nuevo en ese momento (el pico de cada toma queda en el log).
    takeRef.current = { downAt: Date.now(), locked: false };
    review.startRecording();
    live.begin(item.corrected);
    capture
      .start({ rejectOnError: true, onAudio: live.feed })
      .then((blob) => {
        live.end();
        takeRef.current = null; // también si cortó el tope de la toma
        setLocked(false);
        review.stopRecording(blob);
      })
      .catch((err: unknown) => {
        console.warn('[review] mic failed', err);
        live.end();
        takeRef.current = null;
        setLocked(false);
        setMicError(true);
        review.repeat();
      });
  };
  const holdEnd = (): void => {
    const take = takeRef.current;
    if (!take || take.locked) return;
    if (Date.now() - take.downAt < TAP_LOCK_MS) {
      take.locked = true;
      setLocked(true);
      capture.endOnSilence(TAP_SILENCE_MS);
      return;
    }
    endTake();
  };

  const exit = (): void => {
    if (closing) return;
    setClosing(true);
    hush();
    if (postRollRef.current) {
      clearTimeout(postRollRef.current);
      postRollRef.current = null;
    }
    takeRef.current = null;
    if (phase === 'recording') capture.stop();
    void review.close().then((results) => {
      // Un repaso hecho cuenta para la racha: el Inicio de atrás la vuelve a pedir (card #604).
      if (results.length > 0) notifyProgressChanged();
      onExit(results);
    });
  };

  // Lo que pasa, a la vista: si el coach, la tarjeta y el aviso no entran (un
  // celular chico con una frase larga), se desplazan hasta el aviso nuevo; lo
  // que se corre es la línea del coach, que dice lo mismo (card #566). También
  // cuando algo de arriba crece después (la línea del coach cambia, cuándo
  // vuelve llega con el server).
  const fbRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = fbRef.current;
    if (!el || phase === 'ask' || phase === 'recording' || phase === 'checking') return;
    if (typeof el.scrollIntoView !== 'function') return;
    const show = (): void => el.scrollIntoView({ block: 'nearest' });
    show();
    if (typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(show);
    for (const part of el.parentElement?.children ?? [el]) ro.observe(part);
    return () => ro.disconnect();
  }, [phase]);

  // Todas descartadas: no hubo repaso, se cierra como con la X (sin final ni confeti).
  const nothingLeft = phase === 'done' && review.total === 0;
  const exitRef = useRef(exit);
  exitRef.current = exit;
  useEffect(() => {
    if (nothingLeft) exitRef.current();
  }, [nothingLeft]);
  if (nothingLeft) return null;

  if (phase === 'done') {
    return createPortal(
      <Shell source={source} done>
        <DoneScreen
          results={review.results}
          total={review.total}
          ahead={ahead}
          finishLabel={finishLabel}
          closing={closing}
          onExit={exit}
        />
      </Shell>,
      document.body,
    );
  }

  // Lo último que dijo el coach PARA ESTA corrección (con la explicación del juego
  // debajo, la primera vez de la cuenta). Anima (puntitos, karaoke) si es lo que suena.
  const coachBubble =
    review.thread.filter((b) => b.who === 'coach' && b.index === index).at(-1) ?? null;
  const coachLine = coachBubble?.text ?? null;
  const voiceLive =
    coachLine !== null &&
    voicing === coachLine &&
    (player.state === 'loading' || player.state === 'playing');
  // Sin mirar y ya decidida (mal los 3 o «Ver la frase»): «Siguiente» en lugar del micrófono.
  const decidedView =
    round === 'solo' && review.decided && (phase === 'failed' || phase === 'seen');
  const micLabel =
    phase !== 'recording'
      ? copy.review.hold
      : locked
        ? copy.review.holdingLocked
        : copy.review.holding;

  return createPortal(
    <Shell source={source}>
      <Top
        items={items.filter((it) => !review.discardedIds.has(it.id))}
        index={review.position - 1}
        results={review.results}
        onClose={exit}
        onHowTo={() => setHowTo(true)}
        closing={closing}
      />
      {/* Lo que se puede desplazar: la acción (micrófono o «Siguiente») queda fuera,
          fija abajo, para que se vea siempre sin bajar y sin tapar la frase. */}
      <div className={`rv-body${phraseFit(item)}`}>
        {coachLine !== null ? (
          <div className="rv-coach">
            <CoachHead live={voiceLive && player.state === 'playing'} />
            <div className="rv-line" lang="en">
              {voiceLive && slowFor === coachLine ? (
                <SpokenBody
                  text={coachLine}
                  streaming={false}
                  replaying={player.state === 'playing'}
                  getRenderEndIdx={FULL_REVEAL}
                  getCharIndex={getCharIndex}
                />
              ) : voiceLive && player.state === 'loading' ? (
                <Typing />
              ) : voiceLive ? (
                <SpokenBody
                  text={coachLine}
                  streaming
                  getRenderEndIdx={getRenderEndIdx}
                  getCharIndex={getCharIndex}
                />
              ) : (
                coachLine
              )}
            </div>
            {coachBubble?.note && <div className="rv-line-guide">{coachBubble.note}</div>}
          </div>
        ) : (
          <div className="rv-coach-space" />
        )}
        {item && (
          <PhraseCard
            cardRef={cardRef}
            review={review}
            item={item}
            current={current}
            decidedView={decidedView}
            retry={retry}
            live={phase === 'recording' || phase === 'checking' ? live : null}
            onListen={() => speak(item.corrected)}
            showWordMark={source !== 'words'}
          />
        )}
        <div ref={fbRef} className="rv-fb" role="status">
          <Feedback
            review={review}
            item={item}
            current={current}
            locked={locked}
            micError={micError}
          />
        </div>
      </div>
      {waitsForNext ? (
        <button type="button" className="rv-next" onClick={review.celebrationDone}>
          {copy.review.next}
        </button>
      ) : decidedView ? (
        <button type="button" className="rv-next" onClick={review.next}>
          {copy.review.next}
        </button>
      ) : (
        <div className="rv-micwrap">
          <button
            type="button"
            className={`rv-mic${phase === 'recording' ? ' is-rec' : ''}`}
            aria-label={micLabel}
            disabled={!(canTalk || phase === 'recording')}
            onPointerDown={(e) => {
              e.preventDefault();
              // Con el puntero capturado, salirse del botón con el dedo
              // apretado no corta la toma: la corta soltar.
              try {
                e.currentTarget.setPointerCapture(e.pointerId);
              } catch {
                /* noop */
              }
              holdStart();
            }}
            onPointerUp={holdEnd}
            onPointerCancel={holdEnd}
          >
            {phase === 'recording' ? <RecWave /> : <IconMicSolid size={38} />}
          </button>
          <div className="rv-miclabel" aria-hidden="true">
            {micLabel}
          </div>
        </div>
      )}
      {howTo && <HowToSheet onClose={() => setHowTo(false)} />}
    </Shell>,
    document.body,
  );
}

/**
 * Con una frase larga la tarjeta se compacta (letra y casilleros más chicos):
 * en un celular chico el micrófono quedaba debajo de la pantalla (card #566,
 * 20/10). Ninguna frase del repaso pasa de 18 palabras la de una corrección
 * (`REVIEW_SENTENCE_MAX_WORDS`) ni de 12 la de una palabra guardada
 * (`REVIEW_PHRASE_MAX_WORDS`, card #580: el server recorta la oración o el ejemplo
 * largo), así que no pasan de ~105 letras. El escalón de más de 120 letras que sumó #571 para los ejemplos
 * enteros de las palabras guardadas (238 letras) ya no hace falta.
 */
function phraseFit(item: CoachReviewItem | null): string {
  const n = item?.corrected.length ?? 0;
  return n > 80 ? ' is-xlong' : n > 40 ? ' is-long' : '';
}

// `.app`: los tokens de la app viven ahí (no en :root) y el portal sale del shell.
// `done`: el final, con su brillo en toda la pantalla (card #595).
function Shell({
  source,
  done = false,
  children,
}: {
  source: ReviewSource;
  done?: boolean;
  children: ReactNode;
}) {
  const copy = useCopy();
  const tag =
    source === 'day'
      ? copy.review.tagDay
      : source === 'game'
        ? copy.review.tagGame
        : source === 'radar'
          ? copy.review.tagRadar
          : source === 'words'
            ? copy.review.tagWords
            : copy.review.tagClose;
  return (
    <div className="asst-review-backdrop">
      <div className="app asst-review rv" role="dialog" aria-modal="true" aria-label={tag}>
        <div className={`rv-screen${done ? ' is-done' : ''}`}>{children}</div>
      </div>
    </div>
  );
}

/**
 * La X, una barra por corrección (lima = de memoria, violeta = practicada) y
 * «¿Cómo se juega?» (la explicación y las reglas, siempre a mano).
 */
function Top({
  items,
  index,
  results,
  onClose,
  onHowTo,
  closing = false,
}: {
  items: ReadonlyArray<{ id: string }>;
  index: number;
  results: ReadonlyArray<ReviewResult>;
  onClose: () => void;
  onHowTo: () => void;
  closing?: boolean;
}) {
  const copy = useCopy();
  return (
    <div className="rv-top">
      <button
        type="button"
        className="rv-x"
        onClick={onClose}
        disabled={closing}
        aria-label={copy.review.close}
      >
        <IconX size={18} />
      </button>
      <div className="rv-prog" aria-hidden="true">
        {items.map((it, k) => {
          const r = results.find((x) => x.item.id === it.id);
          const cls = r ? `is-${r.outcome}` : k === index ? 'is-cur' : '';
          return <i key={it.id} className={cls} />;
        })}
      </div>
      <button
        type="button"
        className="rv-x rv-howto-btn"
        onClick={onHowTo}
        aria-label={copy.review.howTo}
      >
        ?
      </button>
    </div>
  );
}

/** «¿Cómo se juega?»: la explicación del juego y sus reglas, encima del repaso. */
function HowToSheet({ onClose }: { onClose: () => void }) {
  const copy = useCopy();
  return (
    <div className="rv-howto-backdrop" onClick={onClose}>
      <div
        className="rv-howto"
        role="dialog"
        aria-label={copy.review.howTo}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>{copy.review.howTo}</h2>
        <ol>
          {copy.review.howToSteps.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ol>
        <h3>{copy.review.howToRulesTitle}</h3>
        <ul>
          {copy.review.howToRules.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        <button type="button" className="rv-next" onClick={onClose}>
          {copy.review.howToClose}
        </button>
      </div>
    </div>
  );
}

function CoachHead({ live }: { live: boolean }) {
  return (
    <div className="rv-coach-head">
      <span className={`coach-wave${live ? ' is-live' : ''}`} aria-hidden="true">
        <Waveform color="var(--lime)" size={20} />
      </span>
      <span className="rv-coach-tag">COACH</span>
    </div>
  );
}

function Typing() {
  return (
    <span className="asst-typing rv-typing" role="status" aria-label="Generando respuesta">
      <span />
      <span />
      <span />
    </span>
  );
}

/** Las ondas del micrófono mientras graba. */
function RecWave() {
  return (
    <span className="rv-recwave" aria-hidden="true">
      {[0.55, 0.9, 0.4, 1, 0.65, 0.85, 0.5].map((h, k) => (
        <i key={k} style={{ '--h': h } as CSSProperties} />
      ))}
    </span>
  );
}

function PhraseCard({
  cardRef,
  review,
  item,
  current,
  decidedView,
  retry,
  live,
  onListen,
  showWordMark,
}: {
  cardRef: React.RefObject<HTMLDivElement | null>;
  review: ReturnType<typeof useCoachReview>;
  item: CoachReviewItem;
  current: ReviewResult | undefined;
  decidedView: boolean;
  retry: boolean;
  /** La toma en curso (grabando o comprobando); `null` = no hay toma. */
  live: ReviewLiveState | null;
  onListen: () => void;
  /** Nombrar la palabra guardada de la tarjeta (no en «Decilas ahora», que ya las nombra). */
  showWordMark: boolean;
}) {
  const copy = useCopy();
  const { phase, round, match } = review;
  const solo = round === 'solo';
  const accepted = phase === 'accepted';
  // El español de la frase es la pista que escribió el coach, en las dos vueltas
  // (Emmanuel, 15/10: «debemos cobrarlo todo»; la pista se cobra y queda
  // guardada, `/translate` no se cobra en ningún lado).
  const cue = item.cue;
  const words = assistedVisibleWords(item.corrected);
  // Sin mirar, se dan vuelta los casilleros de lo que ya se oyó, en orden y sin
  // retroceder en la toma (`live.heard`; Emmanuel, 16/10). No el cursor de voz:
  // no sabe QUÉ dijo, y llenaría la frase con sólo hablar.
  const heardNow = live?.heard ?? 0;
  // Bien de otra forma (el juez): se muestra lo que DIJO, no la frase guardada
  // (Emmanuel, 15/10: «me puso en verde lo que debería haber dicho»). La
  // guardada va en la sugerencia, abajo.
  const saidOtherWay =
    accepted && match?.state === 'ok-judged' && review.heard ? review.heard : null;
  const said = saidOtherWay ? (
    <>
      <div className="rv-said-label">{copy.review.youSaid}</div>
      <div className="rv-slots is-reveal" lang="en">
        {assistedVisibleWords(saidOtherWay).map((w, k) => (
          <span key={k}>{w}</span>
        ))}
      </div>
    </>
  ) : null;
  const misses = SOLO_TRIES - review.triesLeft;
  const canAct = phase === 'ask' || phase === 'failed' || phase === 'error';
  const stamp = accepted
    ? !solo
      ? copy.review.stampHelp
      : current?.learned
        ? copy.review.stampLearned
        : retry
          ? copy.review.stampRetry
          : copy.review.stampMemory
    : decidedView
      ? copy.review.stampAgain
      : null;
  const pos = `${review.position}/${review.total}`;
  // De un foco activo del Radar (Emmanuel, 19/10): la marca, con el foco al pasar.
  const radarMark = item.radar ? (
    <div className="rv-radar" title={copy.review.radarOf(item.radar.label)}>
      <IcRadar size={13} />
      {copy.review.radar}
    </div>
  ) : null;
  // Una palabra guardada en el Repaso del día (Emmanuel, 20/10): la tarjeta dice de dónde sale.
  // Sin mirar no la nombra (sería la respuesta); en «Decilas ahora» ya lo dice el título.
  const wordMark =
    item.kind === 'word' && item.word && showWordMark ? (
      <div className="rv-radar">
        <IconBookmarkSmall size={13} />
        {solo ? copy.review.savedWord : copy.review.savedWordOf(item.word)}
      </div>
    ) : null;
  // «Descartar esta frase» (Emmanuel, 19/10): secundario, arriba, lejos del micrófono.
  const discardBtn = canAct ? (
    <button
      type="button"
      className="rv-discard"
      aria-label={copy.card.discardAria}
      title={copy.card.discardAria}
      onClick={review.discard}
    >
      {copy.card.discard}
    </button>
  ) : null;
  return (
    <div
      ref={cardRef}
      className={`rv-card${solo ? ' is-solo' : ''}${accepted ? ' is-win' : ''}${
        phase === 'failed' && match?.state !== 'silence' ? ' is-miss' : ''
      }`}
    >
      {stamp && <div className={`rv-stamp${decidedView ? ' is-again' : ''}`}>{stamp}</div>}
      {solo ? (
        <>
          <div className="rv-card-top">
            <div className="rv-tag">
              {copy.review.tagSolo} · {pos}
              {misses > 0 && !review.decided
                ? ` · ${copy.review.attempt(misses + 1, SOLO_TRIES)}`
                : ''}
            </div>
            {discardBtn}
          </div>
          {radarMark}
          {wordMark}
          {cue && (
            // Sin mirar: qué hay que decir, en español (Emmanuel, 15/10).
            <div className="rv-cue">
              <small>{copy.review.cueLabel}</small>
              <div>«{cue}»</div>
            </div>
          )}
          {/* Un casillero por palabra: se dan vuelta al acertar. */}
          {said ?? (
            <div className={`rv-slots${accepted ? ' is-reveal' : ''}`} aria-hidden={!accepted}>
              {words.map((w, k) => (
                <span key={k} className={k < heardNow ? 'is-on' : undefined}>
                  {w}
                </span>
              ))}
            </div>
          )}
          {decidedView ? (
            <Phrase item={item} small />
          ) : (
            canAct &&
            !review.decided && (
              <button type="button" className="rv-link" onClick={review.seePhrase}>
                {copy.review.seePhrase}
              </button>
            )
          )}
        </>
      ) : (
        <>
          <div className="rv-card-top">
            <div className="rv-tag">
              {copy.review.tagHelp} · {pos}
            </div>
            {discardBtn}
          </div>
          {radarMark}
          {wordMark}
          {said ?? (
            <Phrase
              item={item}
              marks={phase === 'failed' && match ? match.marks : []}
              live={live ? karaokeMarksUpTo(item.corrected, live.heard, live.syllables) : []}
            />
          )}
          {cue && <div className="rv-es">{cue}</div>}
          {canAct && (
            <div className="rv-links">
              <button type="button" className="rv-link" onClick={onListen}>
                {copy.card.listen}
              </button>
              <button type="button" className="rv-link" onClick={review.follow}>
                {copy.review.skip}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * La frase, con lo que cambió la corrección subrayado (y lo que faltó, marcado).
 * Mientras habla, en su lugar, el karaoke de la charla asistida (`live`): fuerte
 * lo oído (en orden, el máximo de la toma), tenue el cursor de voz más allá (el
 * subrayado, también lima, se confundiría con lo ya dicho).
 */
function Phrase({
  item,
  marks = [],
  live = [],
  small = false,
}: {
  item: CoachReviewItem;
  marks?: ReadonlyArray<number>;
  live?: ReadonlyArray<KaraokeMark>;
  small?: boolean;
}) {
  // Una frase de la charla asistida (o una palabra guardada) no tiene nada «corregido» que marcar.
  const changed = new Set(
    item.kind === 'phrase' || item.kind === 'word'
      ? []
      : correctionChangedWords({ said: item.said, corrected: item.corrected }),
  );
  const missed = new Set(marks);
  return (
    <div className={`rv-phrase${small ? ' is-small' : ''}`} lang="en">
      {assistedVisibleWords(item.corrected).map((w, i) => (
        <span key={i}>
          {i > 0 ? ' ' : ''}
          <span
            className={
              live.length > 0
                ? live[i] === 'heard'
                  ? 'rv-heard'
                  : live[i] === 'voice'
                    ? 'rv-voice'
                    : ''
                : [changed.has(i) ? 'rv-ch' : '', missed.has(i) ? 'rv-missed' : '']
                    .filter(Boolean)
                    .join(' ')
            }
          >
            {w}
          </span>
        </span>
      ))}
    </div>
  );
}

/** Lo que pasa, debajo de la tarjeta. Sin mirar y con intentos, no regala la respuesta. */
function Feedback({
  review,
  item,
  current,
  locked,
  micError,
}: {
  review: ReturnType<typeof useCoachReview>;
  item: CoachReviewItem | null;
  current: ReviewResult | undefined;
  locked: boolean;
  micError: boolean;
}) {
  const copy = useCopy();
  const { phase, round, match, heard } = review;
  if (micError) return <>{copy.review.micError}</>;
  const note = soloNote({
    round,
    phase,
    decided: review.decided,
    triesLeft: review.triesLeft,
    silence: match?.state === 'silence',
    result: current,
  }, new Date(), copy);
  const heardLine = heard ? <span className="rv-heard">{copy.card.heard(heard)}</span> : null;
  switch (phase) {
    case 'ask':
      return <>{round === 'solo' ? copy.review.askSolo : copy.review.askHelp}</>;
    case 'recording':
      return (
        <>
          <strong>{copy.review.hearing}</strong>{' '}
          {locked ? copy.review.tapWhenDone : copy.review.releaseWhenDone}
        </>
      );
    case 'checking':
      return <>{copy.review.checking}</>;
    case 'error':
      return (
        <>
          {copy.review.checkError} <span className="rv-again">{copy.review.sayAgain}</span>
        </>
      );
    case 'accepted':
      return (
        <>
          {/* Lo practicado bien con un error NUEVO en el resto (Emmanuel, 20/10:
              «I'd like an improve»): pasa igual y se avisa, sin castigar. */}
          {match?.state === 'ok-judged' && match.judgedSlip && match.judgedPoint && (
            <>
              <strong>{copy.review.slipGood(match.judgedPoint)}</strong>{' '}
              <span className="rv-other">{copy.review.slipNote(match.judgedSlip)}</span>
            </>
          )}
          {/* Dicha de otra forma que igual vale (el juez del server): arriba, lo
              que dijo; acá, la frase guardada, con un porqué si el juez lo dio
              (Emmanuel, 15/10: repetir lo que dijo no decía si era igual o no). */}
          {match?.state === 'ok-judged' && !match.judgedSlip && item && (
            <>
              <strong>{copy.review.ideaOk}</strong>
              <span className="rv-other">
                {match.judgedTip
                  ? copy.review.alsoSay(
                      capitalize(item.corrected),
                      match.judgedTip.replace(/[.\s]+$/, ''),
                    )
                  : copy.review.alsoSayPlain(capitalize(item.corrected))}
              </span>
            </>
          )}
          {note && <strong className="rv-note">{note}</strong>}
        </>
      );
    case 'seen':
      return (
        <>
          <strong>{note}</strong> {item?.explanation ?? ''}
        </>
      );
    case 'failed': {
      if (!match || match.state === 'silence') {
        return (
          <>
            <strong>{copy.card.titleSilence}.</strong> {copy.review.silence}
          </>
        );
      }
      const why = reviewWhy(match, item?.corrected ?? null, review.decided);
      const other = item !== null && assistedOtherPhrase(item.corrected, match.missing);
      if (round === 'solo' && review.decided) {
        return (
          <>
            <strong>{note}</strong> {item?.explanation ?? why.text}
            {heardLine}
          </>
        );
      }
      if (round === 'solo') {
        // Con intentos todavía (Emmanuel, 15/10), «te faltó X» regalaría la
        // respuesta; que volvió el error de la charla, no (sin eso, la decía con
        // el error y no entendía por qué no avanzaba).
        const back = match.state === 'same-as-before' || (match.cameBack?.length ?? 0) > 0;
        return (
          <>
            <strong>{other ? `${copy.card.titleOther}.` : 'Casi.'}</strong>{' '}
            {back && `${copy.review.errorBack} `}
            <span className="rv-tries">{note}</span>
            {/* La regla del error, sin la respuesta (Emmanuel, 15/10: «volvió el
                error» solo no alcanzaba para darse cuenta). */}
            {item?.hint && <span className="rv-hint">{copy.review.hint(item.hint)}</span>}
            {heardLine}
            <span className="rv-again">{copy.review.sayAgain}</span>
          </>
        );
      }
      return (
        <>
          <strong>{why.title}.</strong> {why.text}
          {heardLine}
          <span className="rv-again">{copy.review.sayAgain}</span>
        </>
      );
    }
    default:
      return null;
  }
}

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Qué decir cuando no salió: la vara de las correcciones suma «como antes» y «falta lo corregido».
 * `decided` = mal los 3 (vuelve mañana): sin micrófono, nada de «decila» ni «probá otra vez».
 */
function reviewWhy(
  match: CorrectionReviewMatch,
  corrected: string | null,
  decided = false,
): { title: string; text: string } {
  // Sólo lo de las correcciones (las palabras guardadas no traen textos propios para esto).
  const copy = ASSISTED_COPY.es;
  if (match.state === 'same-as-before') {
    return {
      title: 'Como la primera vez',
      text: decided ? copy.review.decidedSame : copy.review.sameAsBefore,
    };
  }
  // Otra frase entera (encargo 113): «Casi. Te faltó…» no le decía nada.
  if (corrected !== null && assistedOtherPhrase(corrected, match.missing)) {
    return {
      title: copy.card.titleOther,
      text: decided ? copy.review.decidedOther : copy.card.textOther,
    };
  }
  if (match.state === 'missing-correction') {
    const text = match.missingCorrection?.length
      ? copy.review.missingCorrection(match.missingCorrection)
      : copy.review.cameBack((match.cameBack ?? []).join(' '));
    return { title: 'Casi', text };
  }
  const swap = match.missing.length === 1 && match.extra.length === 1;
  const text = swap
    ? copy.card.swap(match.extra[0]!, match.missing[0]!)
    : match.missing.length > 0
      ? copy.card.missing(
          corrected !== null
            ? assistedWordChunks(corrected, match.marks)
            : [match.missing.join(' ')],
        )
      : copy.card.extra(match.extra.join(' '));
  return { title: copy.card.titleRepeat(swap), text };
}

/**
 * El final (la pantalla C que eligió Emmanuel el 22/10, card #604): cuántas de
 * memoria, las frases repasadas con cuándo vuelve cada una y, abajo, los días
 * seguidos y cuántas te tocan mañana. Sin puntos ni la racha de aciertos (el 🔥
 * es el de los días seguidos del perfil). Las que salieron al 2.º o 3.º intento
 * van aparte: no son «de memoria» (card #580, Emmanuel 21/10). Un recuadro en 0
 * no se muestra.
 */
function DoneScreen({
  results,
  total,
  ahead,
  finishLabel,
  closing,
  onExit,
}: {
  results: ReadonlyArray<ReviewResult>;
  total: number;
  /** Los días seguidos y cuántas mañana, del server (`null` mientras llegan o si no llegan). */
  ahead: ReviewAhead | null;
  finishLabel: string;
  closing: boolean;
  onExit: () => void;
}) {
  const copy = useCopy();
  const knownR = results.filter((r) => r.outcome === 'memoria' && !r.retry);
  const retryR = results.filter((r) => r.outcome === 'memoria' && r.retry);
  const practicedR = results.filter((r) => r.outcome === 'practicada');
  const known = knownR.length;
  const learned = results.filter((r) => r.learned).length;
  const failedSave = results.some((r) => !r.saved);
  const pct = total > 0 ? (known / total) * 100 : 0;
  // La fecha del recuadro, si todas las de ese grupo vuelven el mismo día; si no, la de siempre.
  const sameDay = (rs: ReadonlyArray<ReviewResult>, fallback: string): string => {
    const days = new Set(
      rs.map((r) => (r.learned ? null : returnsDay(r.nextReviewAt, new Date(), { month: true }))),
    );
    const only = days.size === 1 ? [...days][0] : null;
    return only ? copy.review.tallyOn(only) : fallback;
  };
  const tally = [
    {
      cls: 'is-known',
      n: known,
      label: copy.review.tallyKnown,
      when: sameDay(knownR, copy.review.tallyKnownWhen),
    },
    {
      cls: 'is-retry',
      n: retryR.length,
      label: copy.review.tallyRetry,
      when: sameDay(retryR, copy.review.tallyRetryWhen),
    },
    {
      cls: 'is-practiced',
      n: practicedR.length,
      label: copy.review.tallyPracticed,
      when: copy.review.tallyPracticedWhen,
    },
  ].filter((t) => t.n > 0);
  const aheadLine = ahead ? copy.review.ahead(ahead.streakDays, ahead.tomorrow) : null;
  return (
    <div className="rv-done" role="status">
      {/* Con la lista, en un celular chico no entra: se desplaza el contenido y «Listo»
          (con los días seguidos) queda fijo abajo, como la acción del repaso (card #566). */}
      <div className="rv-done-body">
        <div className="rv-ring" style={{ '--p': pct } as CSSProperties}>
          <div>
            <span>
              {known}/{total}
              <small>{copy.review.ringLabel}</small>
            </span>
          </div>
        </div>
        <h1>{copy.review.doneTitle}</h1>
        <div className="rv-sub">{copy.review.doneSub(known + retryR.length, total)}</div>
        <div className="rv-tally">
          {tally.map((t) => (
            <div key={t.cls} className={t.cls}>
              <b>{t.n}</b>
              <span>
                {t.label}
                <br />
                {t.when}
              </span>
            </div>
          ))}
        </div>
        {learned > 0 && <div className="rv-extra">⭐ {copy.review.doneLearned(learned)}</div>}
        {failedSave && <div className="rv-extra">{copy.review.saveFailed}</div>}
        <ul className="rv-list">
          {results.map((r) => {
            const when = r.learned ? copy.review.listLearned : returnsDay(r.nextReviewAt);
            return (
              <li key={r.item.id} className={`is-${r.outcome}`}>
                <span className="rv-list-mark" aria-hidden="true">
                  {r.outcome === 'memoria' ? '✓' : '↺'}
                </span>
                <span className="rv-list-text" lang="en">
                  {r.item.corrected}
                </span>
                {when && <span className="rv-list-when">{when}</span>}
              </li>
            );
          })}
        </ul>
      </div>
      {/* Fijo abajo con «Listo»: los días seguidos y mañana se ven siempre (card #604). */}
      {aheadLine && <div className="rv-streak">{aheadLine}</div>}
      <button type="button" className="rv-done-btn" onClick={onExit} disabled={closing}>
        {finishLabel}
      </button>
    </div>
  );
}

const SHAKE: Keyframe[] = [
  { transform: 'translateX(0)' },
  { transform: 'translateX(-9px)' },
  { transform: 'translateX(9px)' },
  { transform: 'translateX(-9px)' },
  { transform: 'translateX(9px)' },
  { transform: 'translateX(0)' },
];
const FLIP: Keyframe[] = [
  { transform: 'perspective(700px) rotateX(0)' },
  { transform: 'perspective(700px) rotateX(90deg)' },
  { transform: 'perspective(700px) rotateX(0)' },
];

/** Una animación de la tarjeta (sacudón al fallar, vuelta al pasar a sin mirar). */
function animate(el: HTMLElement | null, frames: Keyframe[], ms: number): void {
  if (!el || typeof el.animate !== 'function') return;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  el.animate(frames, { duration: ms, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
}
